import {KeyData, KeyHistoryRecord, KeySnapshot} from "../../api/Model";
import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    Zoom
} from "@mui/material";
import {KeyTagChip} from "../../components/key-tag-chip/KeyTagChip";
import {yellow} from "@mui/material/colors";
import {KeyVaultChip} from "../../components/key-vault-chip/KeyVaultChip";
import {KeyLocationShield} from "../../components/location/KeyLocationShield";
import React, {createContext, ReactNode, useCallback, useContext, useMemo, useState} from "react";
import CloseIcon from '@mui/icons-material/Close';
import {daysBetween, useToday} from "../../util/DateUtil";
import {KeyProblems} from "../../api/KeyProblemStore";
import {combineCmp, stringCmp} from "../../util/SortUtil";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import KeyOperationsIcon from "@mui/icons-material/SwapHoriz";
import {KeyOperationDialog} from "./KeyOperationDialog";
import {KeyChip} from "../../components/key-chip/KeyChip";
import {activeRecord, useKeyDataStore} from "../../api/KeyDataStore";
import {KeyHistoryRecordAudit} from "../../components/key-history-record-audit/KeyHistoryRecordAudit";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {EditRecordForm} from "./RecordFormDialog";
import {useDeleteAction} from "../../util/actions/DeleteAction";
import {usePermission} from "../../util/access/UseAccess";
import {KeyData_Admin, KeyData_Operate} from "../../util/access/Permission";
import {
    EmployeeHistoryButton,
    KeyHistoryDialogContext,
} from "../history-dialog/HistoryDialogContext";


export type ChangeLogItem = {
    kind: 'change',
    id: string,
    record: KeyHistoryRecord
} | {
    kind: 'gap',
    id: string,
    from?: string,
    to?: string,
    days?: number,
} | {
    kind: 'overlap',
    id: string,
    from?: string,
    to?: string,
    days?: number
} | {
    kind: 'due',
    id: string,
    date: string,
};

/**
 * Classifies the boundary between a record's `to` and the next record's `from`:
 * - `connected`: clean handover (same day or the day before);
 * - `gap`: the next record starts more than a day later — an unaccounted period;
 * - `overlap`: the next record starts before the previous one ended — the key is in two places at once.
 * Missing dates are treated as a gap (a data anomaly worth surfacing).
 */
function classifyBoundary(prevTo?: string, nextFrom?: string): 'connected' | 'gap' | 'overlap' {
    if ((!prevTo && nextFrom) || (prevTo && !nextFrom)) return 'overlap';
    if (!prevTo || !nextFrom) return 'gap';
    const diff = daysBetween(prevTo, nextFrom);
    if (diff === 0 || diff === 1) return 'connected';
    return diff > 1 ? 'gap' : 'overlap';
}

/** Due (expected return) date of a location, if any. */
function locationDueDate(record: KeyHistoryRecord): string | undefined {
    switch (record.location.type) {
        case 'with-employee':
        case 'with-guest':
            return record.location.expTo;
        default:
            return undefined;
    }
}

/**
 * Builds a chronological list of changes (one per history record). Between two consecutive records
 * that do not connect cleanly, a "gap" or "overlap" item is inserted to flag the inconsistency.
 * If the active record has a due date, a forward-looking "due" item is appended.
 */
export function buildChangeLog(history: KeyHistoryRecord[]): ChangeLogItem[] {
    const sortedHistory = [...history].sort(
        combineCmp<KeyHistoryRecord>(
            stringCmp(it => it.from || '0000-00-00'),
            stringCmp(it => it.to || '9999-99-99'),
            stringCmp(it => it.createdAt || ''),
        ));
    const items: ChangeLogItem[] = [];

    sortedHistory.forEach((record, index) => {
        if (index > 0) {
            const prev = sortedHistory[index - 1];
            const boundary = classifyBoundary(prev.to, record.from);
            if (boundary === 'gap') {
                items.push({
                    kind: 'gap',
                    id: `gap-${prev.id}-${record.id}`,
                    from: prev.to,
                    to: record.from,
                    days: prev.to && record.from ? daysBetween(prev.to, record.from) : undefined,
                });
            } else if (boundary === 'overlap') {
                items.push({
                    kind: 'overlap',
                    id: `overlap-${prev.id}-${record.id}`,
                    from: record.from,
                    to: prev.to,
                    days: prev.to && record.from ? daysBetween(record.from, prev.to) : undefined,
                });
            }
        }
        items.push({kind: 'change', id: `change-${record.id}`, record});
        const dueDate = locationDueDate(record);
        if (!record.to && dueDate) {
            items.push({kind: 'due', id: `due-${record.id}`, date: dueDate});
        }
    });

    return items;
}

function KeyHistoryRecordMenu({keyData, record, onEditRecord, onDeleteRecord}: {
    keyData: KeyData,
    record: KeyHistoryRecord,
    onEditRecord: (keyData: KeyData, record: KeyHistoryRecord) => void,
    onDeleteRecord: (keySnapshot: KeySnapshot) => void,
}) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [operationOpen, setOperationOpen] = useState(false);
    const {canOperate, canAdmin} = usePermission({
        canOperate: KeyData_Operate,
        canAdmin: KeyData_Admin,
    });

    const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    }, [setAnchorEl]);

    const handleCloseMenu = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);

    const handleOpenOperation = useCallback(() => {
        setAnchorEl(null);
        setOperationOpen(true);
    }, [setAnchorEl, setOperationOpen]);

    const handleCloseOperation = useCallback(() => {
        setOperationOpen(false);
    }, [setOperationOpen]);

    const handleEditRecord = useCallback(() => {
        setAnchorEl(null);
        onEditRecord(keyData, record);
    }, [onEditRecord, keyData, record]);

    const handleDeleteRecord = useCallback(() => {
        setAnchorEl(null);
        onDeleteRecord({key: keyData, historyRecord: record});
    }, [onDeleteRecord, keyData, record]);

    return (
        <>
            {canAdmin ? (
                <IconButton size="small" onClick={handleOpenMenu}>
                    <MoreVertIcon fontSize="small"/>
                </IconButton>
            ) : canOperate ? (
                <IconButton size="small" onClick={handleOpenOperation}>
                    <KeyOperationsIcon fontSize="small"/>
                </IconButton>
            ) : null}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
                <MenuItem onClick={handleOpenOperation}>
                    <ListItemIcon>
                        <KeyOperationsIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Key operations</ListItemText>
                </MenuItem>
                <Box sx={{
                    maxWidth: 280,
                    backgroundColor: yellow[50],
                    mx: '4px',
                    border: theme => `1px solid ${theme.palette.warning.dark}`
                }}>
                    <Box sx={{display: 'flex', gap: 2, pl: '11px', pr: 2, py: 1.5}}>
                        <WarningAmberIcon color="warning" fontSize="small"/>
                        <Typography variant="caption" color="text.secondary">
                            <Typography component="span" variant="subtitle2">Danger Zone!</Typography><br/>Use Key
                            operations for regular key changes. Editing or deleting
                            records below is dangerous and can break the key history.
                        </Typography>
                    </Box>
                    <MenuItem onClick={handleEditRecord} sx={{pl: '11px'}}>
                        <ListItemIcon>
                            <EditIcon fontSize="small" color="error"/>
                        </ListItemIcon>
                        <ListItemText>Edit history record</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleDeleteRecord} sx={{pl: '11px'}}>
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" color="error"/>
                        </ListItemIcon>
                        <ListItemText>Delete history record</ListItemText>
                    </MenuItem>
                </Box>
            </Menu>
            <KeyOperationDialog
                keySnapshot={{key: keyData, historyRecord: record}}
                open={operationOpen}
                onClose={handleCloseOperation}
            />
        </>
    );
}

function KeyChangeRow({keyData, record, onEditRecord, onDeleteRecord}: {
    keyData: KeyData,
    record: KeyHistoryRecord,
    onEditRecord: (keyData: KeyData, record: KeyHistoryRecord) => void,
    onDeleteRecord: (keySnapshot: KeySnapshot) => void,
}) {
    const active = !record.to;
    const ToBeforeFromIcon = KeyProblems.problems.toBeforeFrom.icon;
    if (!ToBeforeFromIcon) return null;

    return (
        <TableRow sx={{backgroundColor: active ? yellow[50] : undefined}}>
            <TableCell sx={{fontWeight: active ? 'bold' : undefined}}>
                <Stack direction="row" gap={1} alignItems="center">
                    <Tooltip
                        arrow
                        placement="right"
                        title={`${record.from || "?"} — ${record.to || "present"}`}
                    >
                        <span>
                            {record.from || "?"}
                        </span>
                    </Tooltip>
                </Stack>
            </TableCell>
            <TableCell>
                <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                    {(record.from && record.to && record.to < record.from) && (
                        <Tooltip title={`To date is before from date: ${record.from} — ${record.to}`}>
                            <ToBeforeFromIcon color="warning" fontSize="small"/>
                        </Tooltip>
                    )}
                    <KeyLocationShield location={record.location}/>
                    {record.location.type === 'with-vault' && (
                        <KeyVaultChip keyVault={record.vault} size="small"/>
                    )}
                    {record.location.type === 'with-employee' && (
                        <EmployeeHistoryButton employee={record.location.employee}/>
                    )}
                </Stack>
            </TableCell>
            <TableCell>
                <KeyHistoryRecordAudit record={record}/>
            </TableCell>
            <TableCell sx={{verticalAlign: 'top'}}>
                {record.notes && (
                    <Typography variant="body2">{record.notes}</Typography>
                )}
            </TableCell>
            <TableCell align="right" sx={{verticalAlign: 'top'}} width="48px">
                <KeyHistoryRecordMenu
                    keyData={keyData}
                    record={record}
                    onEditRecord={onEditRecord}
                    onDeleteRecord={onDeleteRecord}
                />
            </TableCell>
        </TableRow>
    );
}

function KeyBoundaryRow({icon, fromDate, fromDateColor, children}: {
    icon: ReactNode,
    fromDate?: string,
    fromDateColor: 'inherit' | 'text.secondary' | 'warning.main',
    children: ReactNode,
}) {
    return (
        <TableRow sx={{backgroundColor: 'action.hover'}}>
            <TableCell sx={{color: fromDateColor}}>
                {fromDate || '?'}
            </TableCell>
            <TableCell colSpan={4}>
                <Stack direction="row" gap={2} alignItems="center">
                    {icon}
                    {children}
                </Stack>
            </TableCell>
        </TableRow>
    );
}

function KeyDueRow({date}: { date: string }) {
    const today = useToday();
    const expired = today > date;
    const DueIcon = KeyProblems.problems.notReturnedKey.icon;
    if (!DueIcon) return null;

    return (
        <KeyBoundaryRow
            fromDate={date}
            fromDateColor={expired ? 'warning.main' : 'text.secondary'}
            icon={<DueIcon fontSize="small" color={expired ? 'warning' : 'disabled'}/>}
        >
            <Typography
                variant="body2"
                sx={{color: expired ? 'warning.main' : 'text.secondary', fontWeight: expired ? 'bold' : undefined}}
            >
                Expected return date
                {expired && ' is overdue'}
            </Typography>
        </KeyBoundaryRow>
    );
}

function KeyGapRow({from, to, days}: {
    from?: string,
    to?: string,
    days?: number,
}) {
    const GapIcon = KeyProblems.problems.historyGap.icon;
    if (!GapIcon) return null;

    return (
        <KeyBoundaryRow
            fromDate={from}
            fromDateColor="warning.main"
            icon={<GapIcon color="warning" fontSize="small"/>}
        >
            <Typography variant="body2" color="text.secondary">
                Gap in history
                {from && to ? `: ${from} — ${to}` : ""}
                {days !== undefined && days > 1 ? ` (${days} days unaccounted)` : ""}
            </Typography>
        </KeyBoundaryRow>
    );
}

function KeyOverlapRow({from, to, days}: {
    from?: string,
    to?: string,
    days?: number,
}) {
    const OverlapIcon = KeyProblems.problems.historyOverlap.icon;
    if (!OverlapIcon) return null;

    return (
        <KeyBoundaryRow
            fromDate={from}
            fromDateColor="warning.main"
            icon={<OverlapIcon color="warning" fontSize="small"/>}
        >
            <Typography variant="body2" color="text.secondary">
                Overlapping records
                {from && to ? `: ${from} — ${to}` : ""}
                {days !== undefined && days > 0 ? ` (${days} days overlap)` : ""}
            </Typography>
        </KeyBoundaryRow>
    );
}

export function KeyHistoryDialog({keyData, open, onClose}: {
    keyData: KeyData,
    open: { open: boolean, x: number; y: number },
    onClose: () => void,
}) {
    const {updateHistoryRecord, deleteHistoryRecord} = useKeyDataStore();

    // const handleCreateRecord = useFormDialogAction({
    //     title: 'Create History Record',
    //     description: 'Enter the details for the new history record.',
    //     submitButtonText: 'Create',
    //     renderForm: CreateRecordForm,
    //     action: (openDialog) => {
    //         const currentActiveRecord = activeRecord(keyData);
    //         openDialog({
    //             initialValue: {
    //                 from: today(),
    //                 vault: currentActiveRecord?.vault || defaultKeyVault,
    //                 location: currentActiveRecord?.location || {type: 'with-vault'},
    //                 notes: '',
    //                 finishPreviousRecords: true,
    //             },
    //             onSubmit: form => createHistoryRecord(keyData, form),
    //             successMessage: `Added new record for key ${keyData.keyNumber}`,
    //             errorMessage: 'Failed to add a new record. Please try again later.',
    //         });
    //     },
    // }, [createHistoryRecord, keyData]);

    const handleUpdateRecord = useFormDialogAction({
        title: 'Edit History Record',
        description: 'Update the history record information below.',
        submitButtonText: 'Update',
        renderForm: EditRecordForm,
        action: (openDialog, key: KeyData, record: KeyHistoryRecord) => openDialog({
            initialValue: {
                from: record.from,
                to: record.to,
                vault: record.vault,
                location: record.location || {type: 'with-vault'},
                notes: record.notes,
            },
            onSubmit: form => updateHistoryRecord(key, record, form),
            successMessage: `Updated record for key ${key.keyNumber}`,
            errorMessage: 'Failed to update record. Please try again later.',
        })
    }, [updateHistoryRecord]);

    const handleDeleteRecord = useDeleteAction({
        description: 'Are you sure you want to delete this history record? This action cannot be undone.',
        action: (snapshot: KeySnapshot) => deleteHistoryRecord(snapshot.key, snapshot.historyRecord),
        successMessage: snapshot => `Deleted history record ${snapshot.historyRecord?.from}`,
        failureMessage: 'Failed to delete history record',
    }, [deleteHistoryRecord]);

    const changeLog = useMemo(
        () => open.open ? buildChangeLog(keyData.history) : [],
        [keyData.history, open.open],
    );

    const keyRecord = activeRecord(keyData);

    return (
        <Dialog
            open={open.open}
            onClose={onClose}
            fullWidth
            maxWidth="xl"
            slots={{
                transition: Zoom,
            }}
            slotProps={{
                transition: {
                    style: {transformOrigin: `${open.x}px ${open.y}px`},
                    timeout: {enter: 300, exit: 300},
                    easing: {enter: "ease-out", exit: "ease-in"},
                },
            }}
        >
            <DialogContent>
                <Stack direction="row" gap={4} alignItems="center" justifyContent="space-between" mb={2}>
                    <Stack direction="row" gap={4} alignItems="center">
                        <Typography variant="h6">
                            Key History
                        </Typography>
                        <KeyChip keyData={keyData} record={keyRecord} size="medium"/>

                        {keyData.tags.length > 0 && (
                            <Stack direction="row" gap={1}>
                                {keyData.tags.map(tag => (
                                    <KeyTagChip keyTag={tag} key={tag.id} size="small"/>
                                ))}
                            </Stack>
                        )}
                        {keyData.transponderId && (
                            <Typography variant="caption" component="span">NFC: {keyData.transponderId}</Typography>
                        )}
                    </Stack>
                    <Stack direction="row" gap={1} alignItems="center">
                        <IconButton onClick={onClose}>
                            <CloseIcon/>
                        </IconButton>
                    </Stack>
                </Stack>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="medium">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 'bold'}} width="120px">Date</TableCell>
                                <TableCell sx={{fontWeight: 'bold'}} width="320px">Location</TableCell>
                                <TableCell sx={{fontWeight: 'bold'}} width="320px">Recorded&nbsp;by</TableCell>
                                <TableCell sx={{fontWeight: 'bold'}}>Notes</TableCell>
                                <TableCell width="48px"/>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {changeLog.slice().reverse().map(item => {
                                switch (item.kind) {
                                    case 'change':
                                        return (
                                            <KeyChangeRow
                                                key={item.id}
                                                keyData={keyData}
                                                record={item.record}
                                                onEditRecord={handleUpdateRecord}
                                                onDeleteRecord={handleDeleteRecord}
                                            />
                                        );
                                    case 'due':
                                        return (
                                            <KeyDueRow key={item.id} date={item.date}/>
                                        );
                                    case "gap":
                                        return (
                                            <KeyGapRow
                                                key={item.id}
                                                from={item.from}
                                                to={item.to}
                                                days={item.days}
                                            />
                                        );
                                    case "overlap":
                                        return (
                                            <KeyOverlapRow
                                                key={item.id}
                                                from={item.from}
                                                to={item.to}
                                                days={item.days}
                                            />
                                        );
                                    default:
                                        return null;
                                }
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
        </Dialog>
    );
}

interface KeyHistoryDialogState {
    keyData: KeyData;
    x: number;
    y: number;
}

interface KeyHistoryDialogRendererContextType {
    dialogState: KeyHistoryDialogState | null;
    handleCloseDialog: () => void;
}

const KeyHistoryDialogRendererContext = createContext<KeyHistoryDialogRendererContextType | null>(null);

export function KeyHistoryDialogProvider({children}: {children: ReactNode}) {
    const [dialogState, setDialogState] = useState<KeyHistoryDialogState | null>(null);

    const openKeyHistoryDialog = useCallback((keyData: KeyData, origin: {x: number, y: number}) => {
        setDialogState({keyData, ...origin});
    }, []);

    const handleCloseDialog = useCallback(() => setDialogState(null), []);
    const contextValue = useMemo(() => ({openKeyHistoryDialog}), [openKeyHistoryDialog]);
    const rendererContextValue = useMemo(
        () => ({dialogState, handleCloseDialog}),
        [dialogState, handleCloseDialog],
    );

    return (
        <KeyHistoryDialogContext value={contextValue}>
            <KeyHistoryDialogRendererContext value={rendererContextValue}>
                {children}
            </KeyHistoryDialogRendererContext>
        </KeyHistoryDialogContext>
    );
}

export function KeyHistoryDialogRenderer() {
    const context = useContext(KeyHistoryDialogRendererContext);
    if (!context) {
        throw new Error('KeyHistoryDialogRenderer must be used within a KeyHistoryDialogProvider');
    }
    const {dialogState, handleCloseDialog} = context;

    if (!dialogState) return null;

    return (
        <KeyHistoryDialog
            keyData={dialogState.keyData}
            open={{open: true, x: dialogState.x, y: dialogState.y}}
            onClose={handleCloseDialog}
        />
    );
}
